import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Badge, Button, ButtonGroup, Card, Col, Container, Form, ProgressBar, Row, Spinner } from "react-bootstrap";
import { useParams, useSearchParams } from "react-router-dom";
import Select from "react-select";

import signatureApi, { publicDocumentUrl, setSignatureApiRoot } from "../../../utils/api/signature";

import "./style.css";

const FERASIGN_LOGO_LIGHT = `${process.env.PUBLIC_URL || ""}/branding/fera/logos/sign/fera-sign-light.svg`;

const FIELD_LABELS = {
  signature: "Signature",
  initials: "Initials",
  date_signed: "Date",
  text: "Text",
  checkbox: "Checkbox",
  name: "Full Name",
  email: "Email",
  radio: "Selection",
  dropdown: "Selection"
};

const SCRIPT_FONTS = [
  {
    value: "segoe-script",
    label: "Segoe Script",
    css: "\"Segoe Script\", \"Brush Script MT\", cursive"
  },
  {
    value: "lucida-handwriting",
    label: "Lucida Handwriting",
    css: "\"Lucida Handwriting\", \"Bradley Hand\", cursive"
  },
  {
    value: "monotype-corsiva",
    label: "Monotype Corsiva",
    css: "\"Monotype Corsiva\", \"Snell Roundhand\", cursive"
  },
  {
    value: "brush-script",
    label: "Brush Script",
    css: "\"Brush Script MT\", \"Segoe Print\", cursive"
  }
];

const SELECT_STYLES = {
  menuPortal: (base) => ({ ...base, zIndex: 1080 })
};
const PDF_PAGE_MAX_WIDTH = 860;
const MIN_ZOOM = 0.65;
const MAX_ZOOM = 1.75;

function today() {
  return new Date().toISOString().slice(0, 10);
}

function displayDate(value) {
  if (!value) return "Not set";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

function fieldLabel(field) {
  return field.label || field.placeholder || FIELD_LABELS[field.fieldType] || "Field";
}

function parseJson(value, fallback) {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value !== "string") return value;

  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

function metadataObject(value) {
  const parsed = parseJson(value, {});
  return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
}

function validationObject(value) {
  const parsed = parseJson(value, {});
  return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
}

function documentPageCount(document) {
  const metadata = metadataObject(document?.metadata);
  return Number(document?.pageCount || metadata.pageCount || metadata.pageSizes?.length || 0);
}

function optionList(field) {
  const parsed = parseJson(field.options, []);
  const options = Array.isArray(parsed) ? parsed : [];
  return options.map((option) => {
    if (option && typeof option === "object") {
      const value = option.value ?? option.label ?? "";
      return { value, label: option.label ?? option.value ?? "" };
    }
    return { value: option, label: String(option) };
  });
}

function selectedOption(options, value) {
  return options.find((option) => String(option.value) === String(value)) || null;
}

function fieldDateValue(field, envelope) {
  const metadata = metadataObject(field.metadata);
  const validation = validationObject(field.validation);
  const format = String(metadata.dateFormat || validation.dateFormat || metadata.format || "date").toLowerCase();
  const timeZone = metadata.timeZone || validation.timeZone || envelope?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const options = format === "time"
    ? { hour: "2-digit", minute: "2-digit", timeZone }
    : format === "date_time" || format === "datetime" || format === "date and time"
      ? { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", timeZone }
      : { year: "numeric", month: "2-digit", day: "2-digit", timeZone };

  try {
    return new Intl.DateTimeFormat(metadata.locale || navigator.language || "en-US", options).format(new Date());
  } catch {
    return today();
  }
}

function isTruthyFieldValue(value) {
  return ["true", "1", "yes", "on", "checked"].includes(String(value).toLowerCase());
}

function isFilledValue(value) {
  return value !== null && value !== undefined && value !== "";
}

function isMissingRequired(field, value) {
  if (!field.required) return false;
  if (field.fieldType === "checkbox") return !isTruthyFieldValue(value);
  return !isFilledValue(value);
}

function fieldCharacterLimit(field) {
  const metadata = metadataObject(field.metadata);
  const validation = validationObject(field.validation);
  const limit = Number(validation.characterLimit || validation.maxLength || metadata.characterLimit || metadata.maxLength);
  return Number.isFinite(limit) && limit > 0 ? limit : null;
}

function fieldIsMultiline(field) {
  const metadata = metadataObject(field.metadata);
  const validation = validationObject(field.validation);
  return Boolean(metadata.multiline || metadata.multiLine || validation.multiline || validation.multiLine);
}

function fieldIsReadOnly(field) {
  const metadata = metadataObject(field.metadata);
  const validation = validationObject(field.validation);
  return Boolean(metadata.readOnly || validation.readOnly || ["name", "email"].includes(field?.fieldType));
}

function fieldTextValue(field, fallback = "") {
  const metadata = metadataObject(field.metadata);
  if (metadata.signatureText) return metadata.signatureText;
  if (field.value === null || field.value === undefined || field.value === "") return fallback;

  const parsed = parseJson(field.value, field.value);
  if (typeof parsed === "string" && parsed.startsWith("data:image/")) return fallback;
  if (parsed && typeof parsed === "object") return parsed.text || parsed.value || parsed.name || fallback;
  return String(parsed || fallback);
}

function initialsForName(name) {
  return String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

function defaultValueFor(field, recipient) {
  if (isFilledValue(field.value)) return fieldTextValue(field);
  if (isFilledValue(field.defaultValue)) return field.defaultValue;
  if (field.fieldType === "name") return recipient?.name || "";
  if (field.fieldType === "email") return recipient?.email || "";
  if (field.fieldType === "signature") return recipient?.name || "";
  if (field.fieldType === "initials") return initialsForName(recipient?.name);
  if (field.fieldType === "date_signed") return today();
  if (field.fieldType === "checkbox") return "false";
  return "";
}

function errorMessage(error, fallback) {
  return error?.response?.data?.error?.message
    || error?.response?.data?.error
    || error?.response?.data?.message
    || error?.message
    || fallback;
}

function signatureStorageKey(email) {
  return `feradel.sign.signature.${String(email || "").trim().toLowerCase()}`;
}

function initialsStorageKey(email) {
  return `feradel.sign.initials.${String(email || "").trim().toLowerCase()}`;
}

function loadStoredSignature(email) {
  if (!email || typeof localStorage === "undefined") return null;
  const parsed = parseJson(localStorage.getItem(signatureStorageKey(email)), null);
  return parsed?.dataUrl ? parsed : null;
}

function loadStoredInitials(email) {
  if (!email || typeof localStorage === "undefined") return null;
  const parsed = parseJson(localStorage.getItem(initialsStorageKey(email)), null);
  return parsed?.dataUrl ? parsed : null;
}

function saveStoredSignature(email, signature) {
  if (!email || typeof localStorage === "undefined" || !signature?.dataUrl) return;
  localStorage.setItem(signatureStorageKey(email), JSON.stringify(signature));
}

function saveStoredInitials(email, initials) {
  if (!email || typeof localStorage === "undefined" || !initials?.dataUrl) return;
  localStorage.setItem(initialsStorageKey(email), JSON.stringify(initials));
}

function fontOption(value) {
  return selectedOption(SCRIPT_FONTS, value) || SCRIPT_FONTS[0];
}

function fontCss(value) {
  return fontOption(value).css;
}

function textFontCss(value) {
  if (value === "times") return "\"Times New Roman\", Times, serif";
  if (value === "courier") return "\"Courier New\", Courier, monospace";
  return "Arial, Helvetica, sans-serif";
}

function textAlignValue(value) {
  return ["left", "center", "right"].includes(value) ? value : "left";
}

function fieldFlexAlignment(value) {
  const align = textAlignValue(value);
  if (align === "right") return "flex-end";
  if (align === "center") return "center";
  return "flex-start";
}

function renderTypedSignatureDataUrl(text, fontValue, compact = false) {
  const canvas = document.createElement("canvas");
  const width = compact ? 360 : 720;
  const height = compact ? 180 : 220;
  const cleanText = String(text || "").trim();
  const font = fontOption(fontValue);
  const context = canvas.getContext("2d");
  let size = compact ? 82 : 74;

  canvas.width = width;
  canvas.height = height;
  context.clearRect(0, 0, width, height);
  context.fillStyle = "#13232f";
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.font = `${size}px ${font.css}`;

  while (context.measureText(cleanText).width > width - 56 && size > 32) {
    size -= 2;
    context.font = `${size}px ${font.css}`;
  }

  context.fillText(cleanText, width / 2, height / 2 + (compact ? 2 : 8));
  return canvas.toDataURL("image/png");
}

function completedByServer(field) {
  return Boolean(field.completedAt || isFilledValue(field.value));
}

function valueImageDataUrl(value) {
  const parsed = parseJson(value, value);
  if (typeof parsed === "string" && parsed.startsWith("data:image/")) return parsed;
  if (parsed && typeof parsed === "object") {
    return parsed.dataUrl || parsed.image || parsed.signature || null;
  }
  return null;
}

function normalizedPercent(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return "0%";
  return `${Math.max(0, Math.min(1, number)) * 100}%`;
}

function fittedFieldFontSize(field, pageSize, preview) {
  if (preview?.kind === "image" || preview?.kind === "checkbox") return null;

  const fieldWidth = Math.max(1, (Number(field.width) || 0.2) * pageSize.width);
  const fieldHeight = Math.max(1, (Number(field.height) || 0.04) * pageSize.height);
  const text = String(preview?.value || "");
  const signature = preview?.kind === "signature";
  const byHeight = fieldHeight * (signature ? 0.7 : 0.48);
  const characterWidthRatio = signature ? 0.48 : 0.56;
  const byWidth = text
    ? Math.max(5, (fieldWidth - 8) / (Math.max(text.length, 1) * characterWidthRatio))
    : byHeight;
  const fitted = Math.min(byHeight, byWidth);

  return Math.max(5, Math.min(signature ? 46 : 28, fitted));
}

function fieldPlacementStyle(field, pageSize, preview) {
  const fontSize = fittedFieldFontSize(field, pageSize, preview);
  const metadata = metadataObject(field.metadata);
  const textAlign = textAlignValue(metadata.textAlign);
  const style = {
    left: normalizedPercent(field.x),
    top: normalizedPercent(field.y),
    width: normalizedPercent(field.width),
    height: normalizedPercent(field.height),
    justifyContent: preview?.kind === "checkbox" ? "center" : fieldFlexAlignment(textAlign),
    textAlign,
    fontFamily: preview?.kind === "signature" ? fontCss(preview.font) : textFontCss(metadata.fontFamily)
  };

  if (fontSize) style.fontSize = `${fontSize}px`;
  return style;
}

function sortFields(fields, documents) {
  const documentOrder = new Map(documents.map((document, index) => [document.uuid, index]));
  return [...fields].sort((left, right) => {
    const documentCompare = (documentOrder.get(left.documentUuid) ?? 0) - (documentOrder.get(right.documentUuid) ?? 0);
    if (documentCompare !== 0) return documentCompare;
    if ((left.pageNumber || 0) !== (right.pageNumber || 0)) return (left.pageNumber || 0) - (right.pageNumber || 0);
    if ((left.fieldOrder ?? 0) !== (right.fieldOrder ?? 0)) return (left.fieldOrder ?? 0) - (right.fieldOrder ?? 0);
    if (Number(left.y || 0) !== Number(right.y || 0)) return Number(left.y || 0) - Number(right.y || 0);
    return Number(left.x || 0) - Number(right.x || 0);
  });
}

function nextGuidedField(guidedFields, completedUuids, afterUuid = "") {
  const remaining = guidedFields.filter((field) => !completedUuids.has(field.uuid));
  if (remaining.length < 1) return null;
  if (!afterUuid) return remaining[0];

  const afterIndex = guidedFields.findIndex((field) => field.uuid === afterUuid);
  const next = remaining.find((field) => guidedFields.findIndex((candidate) => candidate.uuid === field.uuid) > afterIndex);
  return next || remaining[0];
}

function previousGuidedField(guidedFields, beforeUuid = "") {
  if (guidedFields.length < 1) return null;
  const beforeIndex = guidedFields.findIndex((field) => field.uuid === beforeUuid);
  if (beforeIndex <= 0) return guidedFields[guidedFields.length - 1];
  return guidedFields[beforeIndex - 1];
}

function SignatureDrawPad({ value, onChange, disabled }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef(null);

  const pointFor = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const source = event.touches?.[0] || event.changedTouches?.[0] || event;
    return {
      x: (source.clientX - rect.left) * (canvas.width / rect.width),
      y: (source.clientY - rect.top) * (canvas.height / rect.height)
    };
  };

  const start = (event) => {
    if (disabled) return;
    event.preventDefault();
    drawingRef.current = true;
    lastPointRef.current = pointFor(event);
  };

  const move = (event) => {
    if (!drawingRef.current || disabled) return;
    event.preventDefault();
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    const current = pointFor(event);
    const previous = lastPointRef.current || current;

    context.strokeStyle = "#13232f";
    context.lineWidth = 4;
    context.lineCap = "round";
    context.lineJoin = "round";
    context.beginPath();
    context.moveTo(previous.x, previous.y);
    context.lineTo(current.x, current.y);
    context.stroke();
    lastPointRef.current = current;
  };

  const stop = () => {
    if (!drawingRef.current || disabled) return;
    drawingRef.current = false;
    lastPointRef.current = null;
    onChange(canvasRef.current.toDataURL("image/png"));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    context.clearRect(0, 0, canvas.width, canvas.height);
    onChange("");
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !value) return;
    const image = new Image();
    image.onload = () => {
      const context = canvas.getContext("2d");
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
    };
    image.src = value;
  }, [value]);

  return (
    <div className="sign-draw-pad">
      <canvas
        ref={canvasRef}
        className="sign-canvas"
        width="720"
        height="220"
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={stop}
        onMouseLeave={stop}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={stop}
      />
      <div className="mt-2">
        <Button variant="outline-secondary" size="sm" disabled={disabled} onClick={clear}>
          Clear
        </Button>
      </div>
    </div>
  );
}

function PdfRenderedPage({ page, width }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !page || !width) return undefined;

    const viewport = page.getViewport({
      scale: width / page.getViewport({ scale: 1 }).width
    });
    const context = canvas.getContext("2d");
    const outputScale = window.devicePixelRatio || 1;
    let cancelled = false;

    canvas.width = Math.floor(viewport.width * outputScale);
    canvas.height = Math.floor(viewport.height * outputScale);
    canvas.style.width = `${viewport.width}px`;
    canvas.style.height = `${viewport.height}px`;

    const renderTask = page.render({
      canvasContext: context,
      viewport,
      transform: outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : null
    });

    renderTask.promise.catch((error) => {
      if (!cancelled && error?.name !== "RenderingCancelledException") {
        console.error(error);
      }
    });

    return () => {
      cancelled = true;
      renderTask.cancel();
    };
  }, [page, width]);

  return <canvas ref={canvasRef} className="sign-pdf-canvas" />;
}

function PdfDocumentPreview({ token, document, activePageNumber, zoom, renderOverlay, onPagesLoaded }) {
  const measureRef = useRef(null);
  const pageRefs = useRef(new Map());
  const [pages, setPages] = useState([]);
  const [containerWidth, setContainerWidth] = useState(PDF_PAGE_MAX_WIDTH);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const element = measureRef.current;
    if (!element) return undefined;

    const updateWidth = () => {
      setContainerWidth(Math.max(320, element.clientWidth || PDF_PAGE_MAX_WIDTH));
    };

    updateWidth();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateWidth);
      return () => window.removeEventListener("resize", updateWidth);
    }

    const observer = new ResizeObserver(updateWidth);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let cancelled = false;
    let loadingTask = null;

    const loadPdf = async () => {
      if (!token || !document?.uuid) return;

      try {
        setLoading(true);
        setError("");
        setPages([]);
        const response = await signatureApi.get(
          `/public/sign/${encodeURIComponent(token)}/documents/${encodeURIComponent(document.uuid)}/content`,
          { responseType: "arraybuffer" }
        );
        const [pdfjsLib] = await Promise.all([
          import("pdfjs-dist/legacy/build/pdf"),
          import("pdfjs-dist/legacy/build/pdf.worker.entry")
        ]);
        loadingTask = pdfjsLib.getDocument({ data: response.data, disableWorker: true });
        const pdf = await loadingTask.promise;
        const items = [];

        for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
          const page = await pdf.getPage(pageNumber);
          const viewport = page.getViewport({ scale: 1 });
          items.push({
            pageNumber,
            page,
            width: viewport.width,
            height: viewport.height
          });
        }

        if (!cancelled) {
          setPages(items);
          onPagesLoaded?.(items);
        }
      } catch (requestError) {
        if (!cancelled) setError(errorMessage(requestError, "Unable to preview this document."));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadPdf();

    return () => {
      cancelled = true;
      loadingTask?.destroy?.();
    };
  }, [document?.uuid, onPagesLoaded, token]);

  useEffect(() => {
    if (!activePageNumber || pages.length < 1) return;
    window.setTimeout(() => {
      pageRefs.current.get(Number(activePageNumber))?.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
    }, 80);
  }, [activePageNumber, pages.length]);

  const pageWidth = Math.max(280, Math.min(PDF_PAGE_MAX_WIDTH * zoom, Math.floor((containerWidth - 24) * zoom)));

  return (
    <div className="sign-pdf-preview" ref={measureRef}>
      {loading ? <div className="sign-empty-state">Loading document preview...</div> : null}
      {error ? <Alert variant="warning" className="m-3">{error}</Alert> : null}
      {!loading && !error && pages.length < 1 ? (
        <div className="sign-empty-state">No document preview is available.</div>
      ) : null}
      <div className="sign-pdf-pages">
        {pages.map((page) => {
          const pageHeight = pageWidth * (page.height / page.width);
          const pageSize = { width: pageWidth, height: pageHeight };

          return (
            <div
              key={page.pageNumber}
              ref={(node) => {
                if (node) pageRefs.current.set(page.pageNumber, node);
                else pageRefs.current.delete(page.pageNumber);
              }}
              className="sign-pdf-page"
              style={{
                width: pageWidth,
                height: pageHeight
              }}
            >
              <PdfRenderedPage page={page.page} width={pageWidth} />
              {renderOverlay(page.pageNumber, pageSize)}
              <Badge bg="dark" className="sign-pdf-page-badge">{page.pageNumber}</Badge>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PublicSign() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const documentRef = useRef(null);
  const promptRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [savingField, setSavingField] = useState(false);
  const [started, setStarted] = useState(false);
  const [complete, setComplete] = useState(false);
  const [completionResult, setCompletionResult] = useState(null);
  const [declining, setDeclining] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [error, setError] = useState("");
  const [promptError, setPromptError] = useState("");
  const [payload, setPayload] = useState(null);
  const [consentChecked, setConsentChecked] = useState(false);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [completedUuids, setCompletedUuids] = useState(new Set());
  const [currentFieldUuid, setCurrentFieldUuid] = useState("");
  const [activeDocumentUuid, setActiveDocumentUuid] = useState("");
  const [activePageNumber, setActivePageNumber] = useState(1);
  const [loadedPageCount, setLoadedPageCount] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [promptValue, setPromptValue] = useState("");
  const [promptFont, setPromptFont] = useState(SCRIPT_FONTS[0].value);
  const [signatureMode, setSignatureMode] = useState("typed");
  const [drawnValue, setDrawnValue] = useState("");
  const [adoptedSignature, setAdoptedSignature] = useState(null);
  const [adoptedInitials, setAdoptedInitials] = useState(null);
  const [signatureIntentAccepted, setSignatureIntentAccepted] = useState(false);
  const [storedSignature, setStoredSignature] = useState(null);
  const [storedInitials, setStoredInitials] = useState(null);
  const [applyAll, setApplyAll] = useState(false);
  const [promptChecked, setPromptChecked] = useState(false);
  const [useCurrentDate, setUseCurrentDate] = useState(true);

  const selectMenuTarget = typeof document !== "undefined" ? document.body : null;
  const recipient = payload?.recipient || null;
  const envelope = payload?.envelope || null;
  const sender = payload?.sender || {};
  const settings = payload?.settings || {};
  const consent = payload?.consent || {};
  const consentReady = consentAccepted || Boolean(consent.accepted);
  const documents = useMemo(() => payload?.documents || [], [payload]);
  const fields = useMemo(() => payload?.fields || [], [payload]);
  const orderedFields = useMemo(() => sortFields(fields, documents), [fields, documents]);
  const requiredFields = useMemo(() => orderedFields.filter((field) => field.required), [orderedFields]);
  const guidedFields = requiredFields.length > 0 ? requiredFields : orderedFields;
  const activeDocument = documents.find((item) => item.uuid === activeDocumentUuid) || documents[0] || null;
  const currentField = orderedFields.find((field) => field.uuid === currentFieldUuid) || null;
  const remainingFields = guidedFields.filter((field) => !completedUuids.has(field.uuid));
  const sameTypeRemaining = currentField
    ? guidedFields.filter((field) => field.fieldType === currentField.fieldType && !completedUuids.has(field.uuid))
    : [];
  const requiredMissing = requiredFields.filter((field) => !completedUuids.has(field.uuid));
  const completedFieldCount = orderedFields.filter((field) => completedUuids.has(field.uuid)).length;
  const completedRequiredCount = requiredFields.length - requiredMissing.length;
  const canSubmit = consentReady && requiredMissing.length === 0 && !submitting && !savingField;
  const documentOptions = documents.map((document) => ({
    value: document.uuid,
    label: document.displayName || document.originalFileName || "Document"
  }));
  const activeOverlayPageNumber = activeDocument?.uuid === currentField?.documentUuid ? activePageNumber : 1;
  const promptedFields = currentField
    ? (applyAll && ["signature", "initials"].includes(currentField.fieldType) ? sameTypeRemaining : [currentField])
    : [];
  const promptedFieldUuids = new Set(promptedFields.map((field) => field.uuid));
  const activePageCount = Math.max(loadedPageCount, documentPageCount(activeDocument), 1);
  const firstDocument = documents[0] || null;

  useEffect(() => {
    const configuredApi = params.get("api") || params.get("sa") || params.get("ma");
    const instance = params.get("i") || params.get("instance");
    if (configuredApi) setSignatureApiRoot(configuredApi);
    if (instance) localStorage.setItem("instance", instance);
  }, [params]);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        setLoading(true);
        setError("");
        const response = await signatureApi.get(`/public/sign/${encodeURIComponent(token)}`);
        const data = response.data;
        if (!data?.success) throw new Error(data?.error || "Unable to load this signing request.");
        if (!mounted) return;

        const nextCompleted = new Set();
        (data.fields || []).forEach((field) => {
          if (completedByServer(field)) nextCompleted.add(field.uuid);
        });

        setPayload(data);
        setConsentAccepted(Boolean(data.consent?.accepted));
        setConsentChecked(Boolean(data.consent?.accepted));
        setCompletedUuids(nextCompleted);
        setActiveDocumentUuid(data.documents?.[0]?.uuid || "");
      } catch (requestError) {
        if (mounted) setError(errorMessage(requestError, "Unable to load this signing request."));
      } finally {
        if (mounted) setLoading(false);
      }
    };

    if (token) load();

    return () => {
      mounted = false;
    };
  }, [token]);

  useEffect(() => {
    if (!currentField) return;

    const metadata = metadataObject(currentField.metadata);
    const stored = currentField.fieldType === "signature" ? loadStoredSignature(recipient?.email) : null;
    const storedInitialsValue = currentField.fieldType === "initials" ? loadStoredInitials(recipient?.email) : null;
    const nextValue = currentField.fieldType === "date_signed"
      ? fieldDateValue(currentField, envelope)
      : defaultValueFor(currentField, recipient);
    const hasAdoptedSignature = currentField.fieldType === "signature" && adoptedSignature?.dataUrl;
    const hasAdoptedInitials = currentField.fieldType === "initials" && adoptedInitials?.dataUrl;

    setPromptError("");
    setStoredSignature(stored);
    setStoredInitials(storedInitialsValue);
    setSignatureMode(hasAdoptedSignature || hasAdoptedInitials ? "adopted" : stored || storedInitialsValue ? "stored" : "typed");
    setApplyAll(false);
    setDrawnValue("");
    setSignatureIntentAccepted(false);
    setPromptChecked(isTruthyFieldValue(nextValue));
    setUseCurrentDate(currentField.fieldType === "date_signed");
    setPromptValue(nextValue);
    setPromptFont(metadata.signatureFont || stored?.font || storedInitialsValue?.font || SCRIPT_FONTS[0].value);
    setActiveDocumentUuid(currentField.documentUuid || "");
    setActivePageNumber(currentField.pageNumber || 1);

    window.setTimeout(() => {
      documentRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      promptRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, 60);
  }, [adoptedInitials, adoptedSignature, currentField, envelope, recipient]);

  const acceptConsent = async () => {
    if (consentReady) return true;
    if (!consentChecked) {
      setError("Please accept electronic signature consent before continuing.");
      return false;
    }

    const response = await signatureApi.post(`/public/sign/${encodeURIComponent(token)}/consent`, {
      accepted: true,
      consentTextVersion: consent.textVersion,
      consentText: consent.text,
      signerName: recipient?.name,
      signerEmail: recipient?.email,
      envelopeUuid: envelope?.uuid
    });

    setConsentAccepted(true);
    setPayload((current) => current ? {
      ...current,
      consent: {
        ...(current.consent || {}),
        accepted: true,
        acceptedAt: response.data?.consent?.acceptedAt || new Date().toISOString()
      }
    } : current);
    return true;
  };

  const startSigning = async () => {
    try {
      setSubmitting(true);
      setError("");
      setPromptError("");
      const consentOk = await acceptConsent();
      if (!consentOk) return;
      await signatureApi.post(`/public/sign/${encodeURIComponent(token)}/start`);
      setStarted(true);

      const first = nextGuidedField(guidedFields, completedUuids);
      setCurrentFieldUuid(first?.uuid || "");
      if (!first) documentRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch (requestError) {
      setError(errorMessage(requestError, "Unable to start signing."));
    } finally {
      setSubmitting(false);
    }
  };

  const updatePayloadFields = (updates) => {
    const updatesByUuid = new Map(updates.map((update) => [update.uuid, update]));
    setPayload((current) => {
      if (!current) return current;
      return {
        ...current,
        fields: (current.fields || []).map((field) => {
          const update = updatesByUuid.get(field.uuid);
          if (!update) return field;
          return {
            ...field,
            value: update.value,
            completedAt: update.value === null || update.value === "" ? null : new Date().toISOString(),
            metadata: {
              ...metadataObject(field.metadata),
              ...metadataObject(update.metadata),
              signatureMethod: update.signatureMethod || metadataObject(update.metadata).signatureMethod || null
            }
          };
        })
      };
    });
  };

  const buildFieldUpdate = (field) => {
    const metadata = metadataObject(field.metadata);

    if (field.fieldType === "signature") {
      const text = promptValue.trim();
      const stored = storedSignature?.dataUrl ? storedSignature : null;
      const adopted = adoptedSignature?.dataUrl ? adoptedSignature : null;

      if (!signatureIntentAccepted) {
        throw new Error("Confirm that this signature is intended as your legal electronic signature.");
      }

      if (signatureMode === "adopted" && adopted) {
        return {
          uuid: field.uuid,
          value: adopted.dataUrl,
          signatureMethod: adopted.method || "adopted",
          metadata: {
            ...metadata,
            signatureMethod: adopted.method || "adopted",
            signatureFont: adopted.font || promptFont,
            signatureText: adopted.text || text || recipient?.name || "",
            appliedFromStoredSignature: false,
            appliedFromAdoptedSignature: true,
            signatureIntentConsent: true,
            signatureIntentText: "Signer intends this representation as a legal electronic signature."
          }
        };
      }

      if (signatureMode === "stored" && stored) {
        return {
          uuid: field.uuid,
          value: stored.dataUrl,
          signatureMethod: "stored",
          metadata: {
            ...metadata,
            signatureMethod: "stored",
            signatureFont: stored.font || promptFont,
            signatureText: stored.text || text || recipient?.name || "",
            appliedFromStoredSignature: true,
            appliedFromAdoptedSignature: false,
            signatureIntentConsent: true,
            signatureIntentText: "Signer intends this representation as a legal electronic signature."
          }
        };
      }

      if (signatureMode === "drawn") {
        if (!drawnValue) throw new Error("Draw the signature.");
        const capture = {
          dataUrl: drawnValue,
          text: text || recipient?.name || "",
          font: promptFont,
          method: "drawn",
          savedAt: new Date().toISOString()
        };
        setAdoptedSignature(capture);
        saveStoredSignature(recipient?.email, capture);
        return {
          uuid: field.uuid,
          value: drawnValue,
          signatureMethod: "drawn",
          metadata: {
            ...metadata,
            signatureMethod: "drawn",
            signatureFont: promptFont,
            signatureText: capture.text,
            appliedFromStoredSignature: false,
            appliedFromAdoptedSignature: false,
            signatureIntentConsent: true,
            signatureIntentText: "Signer intends this drawn signature as a legal electronic signature."
          }
        };
      }

      if (!text) throw new Error("Enter the signature name.");

      const dataUrl = renderTypedSignatureDataUrl(text, promptFont);
      const capture = { dataUrl, text, font: promptFont, method: "typed", savedAt: new Date().toISOString() };
      setAdoptedSignature(capture);
      saveStoredSignature(recipient?.email, capture);
      return {
        uuid: field.uuid,
        value: dataUrl,
        signatureMethod: "typed",
        metadata: {
          ...metadata,
          signatureMethod: "typed",
          signatureFont: promptFont,
          signatureText: text,
          appliedFromStoredSignature: false,
          appliedFromAdoptedSignature: false,
          signatureIntentConsent: true,
          signatureIntentText: "Signer intends this typed signature as a legal electronic signature."
        }
      };
    }

    if (field.fieldType === "initials") {
      const text = promptValue.trim();
      const stored = storedInitials?.dataUrl ? storedInitials : null;
      const adopted = adoptedInitials?.dataUrl ? adoptedInitials : null;
      if (!signatureIntentAccepted) {
        throw new Error("Confirm that these initials are intended as your legal electronic initials.");
      }
      if (signatureMode === "adopted" && adopted) {
        return {
          uuid: field.uuid,
          value: adopted.dataUrl,
          signatureMethod: adopted.method || "adopted",
          metadata: {
            ...metadata,
            signatureMethod: adopted.method || "adopted",
            signatureFont: adopted.font || promptFont,
            signatureText: adopted.text || text || initialsForName(recipient?.name),
            appliedFromStoredSignature: false,
            appliedFromAdoptedSignature: true,
            signatureIntentConsent: true,
            signatureIntentText: "Signer intends these initials as legal electronic initials."
          }
        };
      }
      if (signatureMode === "stored" && stored) {
        return {
          uuid: field.uuid,
          value: stored.dataUrl,
          signatureMethod: "stored",
          metadata: {
            ...metadata,
            signatureMethod: "stored",
            signatureFont: stored.font || promptFont,
            signatureText: stored.text || text || initialsForName(recipient?.name),
            appliedFromStoredSignature: true,
            appliedFromAdoptedSignature: false,
            signatureIntentConsent: true,
            signatureIntentText: "Signer intends these initials as legal electronic initials."
          }
        };
      }
      if (signatureMode === "drawn") {
        if (!drawnValue) throw new Error("Draw the initials.");
        const capture = {
          dataUrl: drawnValue,
          text: text || initialsForName(recipient?.name),
          font: promptFont,
          method: "drawn",
          savedAt: new Date().toISOString()
        };
        setAdoptedInitials(capture);
        saveStoredInitials(recipient?.email, capture);
        return {
          uuid: field.uuid,
          value: drawnValue,
          signatureMethod: "drawn",
          metadata: {
            ...metadata,
            signatureMethod: "drawn",
            signatureFont: promptFont,
            signatureText: capture.text,
            appliedFromStoredSignature: false,
            appliedFromAdoptedSignature: false,
            signatureIntentConsent: true,
            signatureIntentText: "Signer intends these drawn initials as legal electronic initials."
          }
        };
      }
      if (!text) throw new Error("Enter the initials.");
      const dataUrl = renderTypedSignatureDataUrl(text, promptFont, true);
      const capture = { dataUrl, text, font: promptFont, method: "typed", savedAt: new Date().toISOString() };
      setAdoptedInitials(capture);
      saveStoredInitials(recipient?.email, capture);
      return {
        uuid: field.uuid,
        value: dataUrl,
        signatureMethod: "typed",
        metadata: {
          ...metadata,
          signatureMethod: "typed",
          signatureFont: promptFont,
          signatureText: text,
          appliedFromStoredSignature: false,
          appliedFromAdoptedSignature: false,
          signatureIntentConsent: true,
          signatureIntentText: "Signer intends these typed initials as legal electronic initials."
        }
      };
    }

    if (field.fieldType === "date_signed") {
      const value = useCurrentDate ? fieldDateValue(field, envelope) : promptValue;
      if (!value) throw new Error("Enter the signing date.");
      return {
        uuid: field.uuid,
        value,
        metadata: { valueSummary: value }
      };
    }

    if (field.fieldType === "checkbox") {
      return {
        uuid: field.uuid,
        value: promptChecked ? "true" : "false",
        metadata: { valueSummary: promptChecked ? "checked" : "unchecked" }
      };
    }

    if (isMissingRequired(field, promptValue)) throw new Error(`Complete ${fieldLabel(field)}.`);
    return {
      uuid: field.uuid,
      value: promptValue,
      metadata: { valueSummary: promptValue }
    };
  };

  const applyCurrentField = async () => {
    if (!currentField) return;

    try {
      setSavingField(true);
      setPromptError("");
      setError("");
      const targets = applyAll && ["signature", "initials"].includes(currentField.fieldType)
        ? sameTypeRemaining
        : [currentField];
      const updates = targets.map(buildFieldUpdate);

      await signatureApi.post(`/public/sign/${encodeURIComponent(token)}/fields`, { fields: updates });

      updatePayloadFields(updates);

      const nextCompleted = new Set(completedUuids);
      updates.forEach((update) => {
        if (isFilledValue(update.value)) nextCompleted.add(update.uuid);
      });
      setCompletedUuids(nextCompleted);

      const next = nextGuidedField(guidedFields, nextCompleted, currentField.uuid);
      setCurrentFieldUuid(next?.uuid || "");
    } catch (requestError) {
      setPromptError(errorMessage(requestError, requestError.message || "Unable to save this field."));
    } finally {
      setSavingField(false);
    }
  };

  const completeSigning = async () => {
    if (requiredMissing.length > 0) {
      setError("Please complete each required field before submitting.");
      return;
    }

    try {
      setSubmitting(true);
      setError("");
      const response = await signatureApi.post(`/public/sign/${encodeURIComponent(token)}/complete`);
      setCompletionResult(response.data || null);
      setComplete(true);
    } catch (requestError) {
      setError(errorMessage(requestError, "Unable to complete signing."));
    } finally {
      setSubmitting(false);
    }
  };

  const declineSigning = async () => {
    try {
      setSubmitting(true);
      setError("");
      await signatureApi.post(`/public/sign/${encodeURIComponent(token)}/decline`, {
        reason: declineReason
      });
      setComplete(true);
    } catch (requestError) {
      setError(errorMessage(requestError, "Unable to decline this request."));
    } finally {
      setSubmitting(false);
    }
  };

  const handlePagesLoaded = useCallback((pages) => {
    setLoadedPageCount(pages.length);
  }, []);

  const setNextPage = (direction) => {
    setActivePageNumber((current) => Math.max(1, Math.min(activePageCount, current + direction)));
  };

  const setNextZoom = (direction) => {
    setZoom((current) => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, Math.round((current + direction) * 100) / 100)));
  };

  const promptPreviewFor = (field) => {
    if (field.fieldType === "signature") {
      if (signatureMode === "adopted" && adoptedSignature?.dataUrl) {
        return {
          kind: "image",
          value: adoptedSignature.dataUrl,
          filled: true
        };
      }

      if (signatureMode === "stored" && storedSignature?.dataUrl) {
        return {
          kind: "image",
          value: storedSignature.dataUrl,
          filled: true
        };
      }

      if (signatureMode === "drawn" && drawnValue) {
        return {
          kind: "image",
          value: drawnValue,
          filled: true
        };
      }

      return {
        kind: "signature",
        value: promptValue.trim() || recipient?.name || fieldLabel(field),
        font: promptFont,
        filled: Boolean(promptValue.trim())
      };
    }

    if (field.fieldType === "initials") {
      if (signatureMode === "adopted" && adoptedInitials?.dataUrl) {
        return {
          kind: "image",
          value: adoptedInitials.dataUrl,
          filled: true
        };
      }

      if (signatureMode === "stored" && storedInitials?.dataUrl) {
        return {
          kind: "image",
          value: storedInitials.dataUrl,
          filled: true
        };
      }

      if (signatureMode === "drawn" && drawnValue) {
        return {
          kind: "image",
          value: drawnValue,
          filled: true
        };
      }

      return {
        kind: "signature",
        value: promptValue.trim() || initialsForName(recipient?.name) || fieldLabel(field),
        font: promptFont,
        filled: Boolean(promptValue.trim())
      };
    }

    if (field.fieldType === "date_signed") {
      const value = useCurrentDate ? fieldDateValue(field, envelope) : promptValue;
      return {
        kind: "text",
        value: value || fieldLabel(field),
        filled: Boolean(value)
      };
    }

    if (field.fieldType === "checkbox") {
      return {
        kind: "checkbox",
        value: promptChecked,
        filled: promptChecked
      };
    }

    return {
      kind: "text",
      value: promptValue || fieldLabel(field),
      filled: Boolean(promptValue)
    };
  };

  const storedPreviewFor = (field) => {
    const metadata = metadataObject(field.metadata);
    const image = valueImageDataUrl(field.value);
    if (image) {
      return {
        kind: "image",
        value: image,
        filled: true
      };
    }

    if (field.fieldType === "checkbox") {
      const checked = isTruthyFieldValue(field.value);
      return {
        kind: "checkbox",
        value: checked,
        filled: checked
      };
    }

    const text = fieldTextValue(field);
    if (text) {
      return {
        kind: field.fieldType === "signature" || field.fieldType === "initials" ? "signature" : "text",
        value: text,
        font: metadata.signatureFont || SCRIPT_FONTS[0].value,
        filled: true
      };
    }

    return {
      kind: "placeholder",
      value: FIELD_LABELS[field.fieldType] || fieldLabel(field),
      filled: false
    };
  };

  const overlayPreviewFor = (field) => (
    promptedFieldUuids.has(field.uuid) ? promptPreviewFor(field) : storedPreviewFor(field)
  );

  const renderOverlayValue = (field, preview) => {
    if (preview.kind === "image") {
      return <img src={preview.value} alt="" />;
    }

    if (preview.kind === "checkbox") {
      return <span className={`sign-overlay-check ${preview.value ? "is-checked" : ""}`} />;
    }

    if (preview.kind === "signature") {
      return (
        <span
          className="sign-overlay-signature-text"
          style={{ fontFamily: fontCss(preview.font) }}
        >
          {preview.value}
        </span>
      );
    }

    return <span>{preview.value}</span>;
  };

  const renderDocumentOverlay = (pageNumber, pageSize) => {
    if (!activeDocument) return null;

    const pageFields = orderedFields.filter((field) => (
      field.documentUuid === activeDocument.uuid &&
      Number(field.pageNumber || 1) === Number(pageNumber)
    ));
    if (pageFields.length < 1) return null;

    return (
      <div className="sign-document-overlay" aria-hidden="true">
        <div className="sign-overlay-page">
          {pageFields.map((field) => {
            const preview = overlayPreviewFor(field);
            const isPrompted = promptedFieldUuids.has(field.uuid);
            const isCompleted = completedUuids.has(field.uuid) || preview.filled;

            return (
              <div
                key={field.uuid}
                className={[
                  "sign-overlay-field",
                  isPrompted ? "is-prompted" : "",
                  isCompleted ? "is-filled" : "",
                  field.uuid === currentField?.uuid ? "is-current" : ""
                ].filter(Boolean).join(" ")}
                style={fieldPlacementStyle(field, pageSize, preview)}
              >
                {renderOverlayValue(field, preview)}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderFontSelect = () => (
    <div className="top-label">
      <Select
        classNamePrefix="react-select"
        value={selectedOption(SCRIPT_FONTS, promptFont)}
        options={SCRIPT_FONTS}
        isDisabled={savingField || submitting}
        menuPortalTarget={selectMenuTarget}
        styles={SELECT_STYLES}
        onChange={(option) => setPromptFont(option?.value || SCRIPT_FONTS[0].value)}
      />
      <label>STYLE</label>
    </div>
  );

  const renderSignatureModeButtons = (kind) => {
    const hasAdopted = kind === "initials" ? adoptedInitials?.dataUrl : adoptedSignature?.dataUrl;
    const hasStored = kind === "initials" ? storedInitials?.dataUrl : storedSignature?.dataUrl;

    return (
      <ButtonGroup className="sign-capture-mode" size="sm">
        {hasAdopted ? (
          <Button
            variant={signatureMode === "adopted" ? "primary" : "outline-primary"}
            disabled={savingField || submitting}
            onClick={() => setSignatureMode("adopted")}
          >
            Adopted
          </Button>
        ) : null}
        {hasStored ? (
          <Button
            variant={signatureMode === "stored" ? "primary" : "outline-primary"}
            disabled={savingField || submitting}
            onClick={() => {
              setSignatureMode("stored");
            }}
          >
            Saved
          </Button>
        ) : null}
        <Button
          variant={signatureMode === "typed" ? "primary" : "outline-primary"}
          disabled={savingField || submitting}
          onClick={() => {
            setSignatureMode("typed");
          }}
        >
          Type
        </Button>
        <Button
          variant={signatureMode === "drawn" ? "primary" : "outline-primary"}
          disabled={savingField || submitting}
          onClick={() => {
            setSignatureMode("drawn");
          }}
        >
          Draw
        </Button>
      </ButtonGroup>
    );
  };

  const renderSignaturePrompt = () => (
    <div className="sign-prompt-stack">
      {renderSignatureModeButtons("signature")}

      {signatureMode === "adopted" && adoptedSignature?.dataUrl ? (
        <div className="sign-stored-signature">
          <div className="small text-muted">Using signature adopted in this envelope.</div>
          <img src={adoptedSignature.dataUrl} alt="Adopted signature preview" />
        </div>
      ) : null}

      {signatureMode === "stored" && storedSignature?.dataUrl ? (
        <div className="sign-stored-signature">
          <div className="small text-muted">Using saved signature for {recipient?.email || "this email"}.</div>
          <img src={storedSignature.dataUrl} alt="Saved signature preview" />
        </div>
      ) : null}

      {signatureMode === "drawn" ? (
        <>
          <div className="top-label">
            <Form.Control
              value={promptValue}
              placeholder=" "
              disabled={savingField || submitting}
              onChange={(event) => setPromptValue(event.target.value)}
            />
            <label>SIGNATURE NAME</label>
          </div>
          <SignatureDrawPad
            value={drawnValue}
            disabled={savingField || submitting}
            onChange={setDrawnValue}
          />
        </>
      ) : null}

      {signatureMode === "typed" ? (
        <>
          <div className="top-label">
            <Form.Control
              value={promptValue}
              placeholder=" "
              disabled={savingField || submitting}
              onChange={(event) => setPromptValue(event.target.value)}
            />
            <label>SIGNATURE NAME</label>
          </div>
          {renderFontSelect()}
          {promptValue.trim() ? (
            <div className="sign-signature-preview">
              <span style={{ fontFamily: fontCss(promptFont) }}>{promptValue}</span>
            </div>
          ) : null}
        </>
      ) : null}

      <Form.Check
        type="checkbox"
        id="signature-intent"
        label="I intend this as my legal electronic signature."
        checked={signatureIntentAccepted}
        disabled={savingField || submitting}
        onChange={(event) => setSignatureIntentAccepted(event.target.checked)}
      />
    </div>
  );

  const renderInitialsPrompt = () => (
    <div className="sign-prompt-stack">
      {renderSignatureModeButtons("initials")}

      {signatureMode === "adopted" && adoptedInitials?.dataUrl ? (
        <div className="sign-stored-signature">
          <div className="small text-muted">Using initials adopted in this envelope.</div>
          <img src={adoptedInitials.dataUrl} alt="Adopted initials preview" />
        </div>
      ) : null}

      {signatureMode === "stored" && storedInitials?.dataUrl ? (
        <div className="sign-stored-signature">
          <div className="small text-muted">Using saved initials for {recipient?.email || "this email"}.</div>
          <img src={storedInitials.dataUrl} alt="Saved initials preview" />
        </div>
      ) : null}

      {signatureMode === "drawn" ? (
        <>
          <div className="top-label">
            <Form.Control
              value={promptValue}
              placeholder=" "
              disabled={savingField || submitting}
              onChange={(event) => setPromptValue(event.target.value.toUpperCase())}
            />
            <label>INITIALS</label>
          </div>
          <SignatureDrawPad
            value={drawnValue}
            disabled={savingField || submitting}
            onChange={setDrawnValue}
          />
        </>
      ) : null}

      {signatureMode === "typed" ? (
        <>
          <div className="top-label">
            <Form.Control
              value={promptValue}
              placeholder=" "
              disabled={savingField || submitting}
              onChange={(event) => setPromptValue(event.target.value.toUpperCase())}
            />
            <label>INITIALS</label>
          </div>
          {renderFontSelect()}
          {promptValue.trim() ? (
            <div className="sign-signature-preview is-compact">
              <span style={{ fontFamily: fontCss(promptFont) }}>{promptValue}</span>
            </div>
          ) : null}
        </>
      ) : null}

      <Form.Check
        type="checkbox"
        id="initials-intent"
        label="I intend these as my legal electronic initials."
        checked={signatureIntentAccepted}
        disabled={savingField || submitting}
        onChange={(event) => setSignatureIntentAccepted(event.target.checked)}
      />
    </div>
  );

  const renderDatePrompt = () => (
    <div className="sign-prompt-stack">
      <Form.Check
        type="checkbox"
        id="use-current-date"
        label={`Use current date/time (${currentField ? fieldDateValue(currentField, envelope) : today()})`}
        checked={useCurrentDate}
        disabled={savingField || submitting}
        onChange={(event) => {
          setUseCurrentDate(event.target.checked);
          if (event.target.checked && currentField) setPromptValue(fieldDateValue(currentField, envelope));
        }}
      />
      {!useCurrentDate ? (
        <div className="top-label">
          <Form.Control
            type="date"
            value={promptValue}
            placeholder=" "
            disabled={savingField || submitting}
            onChange={(event) => setPromptValue(event.target.value)}
          />
          <label>DATE</label>
        </div>
      ) : null}
    </div>
  );

  const renderStandardPrompt = () => {
    const options = currentField ? optionList(currentField) : [];
    const characterLimit = currentField ? fieldCharacterLimit(currentField) : null;
    const multiline = currentField ? fieldIsMultiline(currentField) : false;

    if (currentField?.fieldType === "checkbox") {
      return (
        <Form.Check
          type="checkbox"
          label={currentField.placeholder || fieldLabel(currentField)}
          checked={promptChecked}
          disabled={savingField || submitting}
          onChange={(event) => setPromptChecked(event.target.checked)}
        />
      );
    }

    if (currentField?.fieldType === "dropdown") {
      return (
        <div className="top-label">
          <Select
            classNamePrefix="react-select"
            value={selectedOption(options, promptValue)}
            options={options}
            isClearable
            isDisabled={savingField || submitting}
            menuPortalTarget={selectMenuTarget}
            styles={SELECT_STYLES}
            onChange={(option) => setPromptValue(option?.value || "")}
          />
          <label>{fieldLabel(currentField)}</label>
        </div>
      );
    }

    if (currentField?.fieldType === "radio") {
      return (
        <div className="sign-radio-group">
          {options.map((option) => (
            <Form.Check
              key={String(option.value)}
              type="radio"
              name={currentField.uuid}
              label={option.label}
              checked={String(promptValue) === String(option.value)}
              disabled={savingField || submitting}
              onChange={() => setPromptValue(option.value)}
            />
          ))}
        </div>
      );
    }

    return (
      <div className="top-label">
        <Form.Control
          as={multiline ? "textarea" : undefined}
          rows={multiline ? 4 : undefined}
          value={promptValue}
          placeholder=" "
          maxLength={characterLimit || undefined}
          readOnly={fieldIsReadOnly(currentField)}
          disabled={savingField || submitting}
          onChange={(event) => setPromptValue(event.target.value)}
        />
        <label>{currentField ? fieldLabel(currentField) : "FIELD"}</label>
        {characterLimit ? (
          <div className="sign-character-count">
            {String(promptValue || "").length}/{characterLimit}
          </div>
        ) : null}
      </div>
    );
  };

  const renderCurrentPrompt = () => {
    if (!currentField) {
      return (
        <div className="sign-ready-panel">
          <div className="sign-ready-title">Ready to submit</div>
          <div className="text-muted">All required fields have been completed.</div>
        </div>
      );
    }

    return (
      <div className="sign-current-field" ref={promptRef}>
        <div className="sign-field-header">
          <div>
            <div className="sign-field-title">{fieldLabel(currentField)}</div>
            <div className="sign-field-subtitle">
              {FIELD_LABELS[currentField.fieldType] || currentField.fieldType}
            </div>
          </div>
          <div className="d-flex gap-2 align-items-center">
            {currentField.required ? <Badge bg="light" text="dark">Required</Badge> : null}
            <Badge bg="secondary">Page {currentField.pageNumber}</Badge>
          </div>
        </div>

        {currentField.fieldType === "signature" ? renderSignaturePrompt() : null}
        {currentField.fieldType === "initials" ? renderInitialsPrompt() : null}
        {currentField.fieldType === "date_signed" ? renderDatePrompt() : null}
        {!["signature", "initials", "date_signed"].includes(currentField.fieldType) ? renderStandardPrompt() : null}

        {["signature", "initials"].includes(currentField.fieldType) && sameTypeRemaining.length > 1 ? (
          <Form.Check
            className="mt-3"
            type="checkbox"
            id="apply-all-signatures"
            label={`Apply to all ${sameTypeRemaining.length} remaining ${FIELD_LABELS[currentField.fieldType].toLowerCase()} fields`}
            checked={applyAll}
            disabled={savingField || submitting}
            onChange={(event) => setApplyAll(event.target.checked)}
          />
        ) : null}

        {promptError ? <Alert variant="danger" className="mt-3 mb-0">{promptError}</Alert> : null}

        <div className="sign-prompt-actions">
          <Button variant="primary" disabled={savingField || submitting} onClick={applyCurrentField}>
            {savingField ? "Applying..." : "OK"}
          </Button>
          <Button
            variant="outline-secondary"
            disabled={savingField || submitting || guidedFields.length < 2}
            onClick={() => setCurrentFieldUuid(previousGuidedField(guidedFields, currentField.uuid)?.uuid || "")}
          >
            Previous
          </Button>
          <Button
            variant="outline-secondary"
            disabled={savingField || submitting || remainingFields.length < 2}
            onClick={() => setCurrentFieldUuid(nextGuidedField(guidedFields, completedUuids, currentField.uuid)?.uuid || "")}
          >
            Next
          </Button>
        </div>
      </div>
    );
  };

  const renderOpeningScreen = () => (
    <main className="sign-public-page">
      <Container className="sign-opening-shell">
        <div className="sign-opening-panel">
          <img className="sign-brand-logo" src={FERASIGN_LOGO_LIGHT} alt="FeraSign" />
          <h1>{envelope?.title || envelope?.subject || envelope?.displayName || "Review and Sign"}</h1>

          <div className="sign-opening-grid">
            <div>
              <div className="sign-opening-label">SENDER</div>
              <div className="sign-opening-value">{sender.name || "Feradel"}</div>
              {sender.firm ? <div className="text-muted">{sender.firm}</div> : null}
            </div>
            <div>
              <div className="sign-opening-label">SIGNER</div>
              <div className="sign-opening-value">{recipient?.name}</div>
              <div className="text-muted">{recipient?.email}</div>
            </div>
            <div>
              <div className="sign-opening-label">DOCUMENTS</div>
              <div className="sign-opening-value">{documents.length}</div>
            </div>
            <div>
              <div className="sign-opening-label">EXPIRES</div>
              <div className="sign-opening-value">{displayDate(envelope?.expirationAt)}</div>
            </div>
          </div>

          {envelope?.messageBody ? (
            <div className="sign-opening-message">
              {envelope.messageBody}
            </div>
          ) : null}

          {error ? <Alert variant="danger">{error}</Alert> : null}

          {declining ? (
            <div className="sign-opening-decline">
              <div className="top-label mb-3">
                <Form.Control
                  as="textarea"
                  rows="3"
                  value={declineReason}
                  placeholder=" "
                  disabled={submitting}
                  onChange={(event) => setDeclineReason(event.target.value)}
                />
                <label>DECLINE REASON</label>
              </div>
              <div className="d-flex flex-wrap gap-2">
                <Button variant="danger" disabled={submitting} onClick={declineSigning}>
                  Confirm Decline
                </Button>
                <Button variant="outline-secondary" disabled={submitting} onClick={() => setDeclining(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="sign-consent-box">
                <Form.Check
                  type="checkbox"
                  id="electronic-signature-consent"
                  label={consent.text || "I agree to use electronic signatures for this envelope."}
                  checked={consentChecked || consentReady}
                  disabled={submitting || consentReady}
                  onChange={(event) => setConsentChecked(event.target.checked)}
                />
                <div className="small text-muted mt-2">
                  Consent version {consent.textVersion || "current"}
                </div>
              </div>

              <div className="sign-opening-actions">
                <Button variant="primary" disabled={submitting || (!consentChecked && !consentReady)} onClick={startSigning}>
                  {submitting ? "Continuing..." : "Continue"}
                </Button>
                {settings.declineEnabled !== false ? (
                  <Button variant="outline-danger" disabled={submitting} onClick={() => setDeclining(true)}>
                    Decline
                  </Button>
                ) : null}
                {settings.downloadBeforeSigningAllowed && firstDocument ? (
                  <Button
                    as="a"
                    variant="outline-secondary"
                    href={publicDocumentUrl(token, firstDocument.uuid)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Download
                  </Button>
                ) : null}
                {settings.contactSenderEnabled !== false && sender.email ? (
                  <Button as="a" variant="outline-secondary" href={`mailto:${sender.email}`}>
                    Contact Sender
                  </Button>
                ) : null}
              </div>
            </>
          )}
        </div>
      </Container>
    </main>
  );

  if (loading) {
    return (
      <main className="sign-public-page sign-centered">
        <Spinner animation="border" />
      </main>
    );
  }

  if (error && !payload) {
    return (
      <main className="sign-public-page">
        <Container className="sign-narrow">
          <Alert variant="danger" className="shadow-sm mb-0">{error}</Alert>
        </Container>
      </main>
    );
  }

  if (complete) {
    return (
      <main className="sign-public-page">
        <Container className="sign-narrow">
          <div className="sign-completion-panel">
            <Alert variant="success" className="mb-3">
              Your signing session is complete.
            </Alert>
            {completionResult?.remainingSigners > 0 ? (
              <p className="text-muted mb-3">
                {completionResult.remainingSigners} additional signer{completionResult.remainingSigners === 1 ? "" : "s"} remain.
              </p>
            ) : (
              <p className="text-muted mb-3">
                A final copy will be emailed after the envelope is fully executed.
              </p>
            )}
            <div className="d-flex flex-wrap gap-2">
              {settings.downloadAfterCompletionAllowed && firstDocument ? (
                <Button
                  as="a"
                  variant="primary"
                  href={publicDocumentUrl(token, firstDocument.uuid)}
                  target="_blank"
                  rel="noreferrer"
                >
                  Download
                </Button>
              ) : null}
              {/*!recipient?.authUser?.exists ? (
                <Button as="a" variant="outline-primary" href="/register">
                  Create Feradel Account
                </Button>
              ) : null*/}
              {settings.contactSenderEnabled !== false && sender.email ? (
                <Button as="a" variant="outline-secondary" href={`mailto:${sender.email}`}>
                  Contact Sender
                </Button>
              ) : null}
            </div>
          </div>
        </Container>
      </main>
    );
  }

  if (!started && !consentReady) {
    return renderOpeningScreen();
  }

  return (
    <main className="sign-public-page">
      <Container fluid="xl">
        <div className="sign-request-header">
          <div>
            <img className="sign-brand-logo" src={FERASIGN_LOGO_LIGHT} alt="FeraSign" />
            <h1>{payload?.envelope?.subject || payload?.envelope?.displayName || "Review and Sign"}</h1>
            <div className="sign-recipient">
              {recipient?.name} &lt;{recipient?.email}&gt;
            </div>
          </div>
          <Badge bg="light" text="dark">{payload?.envelope?.status || "active"}</Badge>
        </div>

        {error ? <Alert variant="danger">{error}</Alert> : null}

        <Row className="g-4 align-items-start">
          <Col lg="8">
            <div className="sign-document-tools">
              {documents.length > 1 ? (
                <div className="top-label sign-document-select">
                  <Select
                    classNamePrefix="react-select"
                    value={selectedOption(documentOptions, activeDocument?.uuid)}
                    options={documentOptions}
                    isClearable={false}
                    menuPortalTarget={selectMenuTarget}
                    styles={SELECT_STYLES}
                    onChange={(option) => {
                      setActiveDocumentUuid(option?.value || "");
                      setActivePageNumber(1);
                    }}
                  />
                  <label>DOCUMENT</label>
                </div>
              ) : null}
              <div className="sign-viewer-controls">
                <ButtonGroup size="sm">
                  <Button variant="outline-secondary" disabled={activePageNumber <= 1} onClick={() => setNextPage(-1)}>
                    Previous Page
                  </Button>
                  <Button variant="outline-secondary" disabled={activePageNumber >= activePageCount} onClick={() => setNextPage(1)}>
                    Next Page
                  </Button>
                </ButtonGroup>
                <span className="sign-page-counter">
                  Page {activePageNumber} of {activePageCount}
                </span>
                <ButtonGroup size="sm">
                  <Button variant="outline-secondary" disabled={zoom <= MIN_ZOOM} onClick={() => setNextZoom(-0.1)}>
                    Zoom Out
                  </Button>
                  <Button variant="outline-secondary" disabled>
                    {Math.round(zoom * 100)}%
                  </Button>
                  <Button variant="outline-secondary" disabled={zoom >= MAX_ZOOM} onClick={() => setNextZoom(0.1)}>
                    Zoom In
                  </Button>
                </ButtonGroup>
              </div>
            </div>
            <div className="sign-document-frame" ref={documentRef}>
              {activeDocument ? (
                <PdfDocumentPreview
                  token={token}
                  document={activeDocument}
                  activePageNumber={activeOverlayPageNumber}
                  zoom={zoom}
                  renderOverlay={renderDocumentOverlay}
                  onPagesLoaded={handlePagesLoaded}
                />
              ) : (
                <div className="sign-empty-state">No document is available.</div>
              )}
            </div>
          </Col>

          <Col lg="4">
            <Card className="sign-action-card">
              <Card.Body>
                <div className="d-flex align-items-start justify-content-between gap-3">
                  <div>
                    <Card.Title>Signing</Card.Title>
                    <Card.Text className="text-muted mb-0">
                      {requiredMissing.length > 0
                        ? `${requiredMissing.length} required field${requiredMissing.length === 1 ? "" : "s"} remaining`
                        : "Ready to submit"}
                    </Card.Text>
                  </div>
                  <Badge bg={started ? "success" : "light"} text={started ? undefined : "dark"}>
                    {started ? "Started" : "New"}
                  </Badge>
                </div>
                <div className="sign-progress-stack">
                  <ProgressBar
                    now={requiredFields.length ? (completedRequiredCount / requiredFields.length) * 100 : 100}
                    aria-label="Required field progress"
                  />
                  <div className="sign-progress-counts">
                    <span>{completedRequiredCount}/{requiredFields.length} required</span>
                    <span>{completedFieldCount}/{orderedFields.length} total</span>
                  </div>
                </div>
              </Card.Body>

              <div className="sign-prompt-panel">
                {started ? renderCurrentPrompt() : (
                  <div className="sign-ready-panel">
                    <div className="sign-ready-title">Start signing</div>
                    <div className="text-muted">The first required field is ready.</div>
                  </div>
                )}
              </div>

              <Card.Body className="sign-submit-panel">
                {declining ? (
                  <>
                    <div className="top-label mb-3">
                      <Form.Control
                        as="textarea"
                        rows="3"
                        value={declineReason}
                        placeholder=" "
                        disabled={submitting || savingField}
                        onChange={(event) => setDeclineReason(event.target.value)}
                      />
                      <label>DECLINE REASON</label>
                    </div>
                    <div className="d-flex gap-2">
                      <Button variant="danger" disabled={submitting || savingField} onClick={declineSigning}>
                        Confirm Decline
                      </Button>
                      <Button variant="outline-secondary" disabled={submitting || savingField} onClick={() => setDeclining(false)}>
                        Cancel
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="d-grid gap-2">
                    {!started ? (
                      <Button variant="primary" disabled={submitting} onClick={startSigning}>
                        Start Signing
                      </Button>
                    ) : null}
                    <Button variant="primary" disabled={!canSubmit} onClick={completeSigning}>
                      {submitting ? "Submitting..." : "Submit Signature"}
                    </Button>
                    <Button variant="outline-danger" disabled={submitting || savingField} onClick={() => setDeclining(true)}>
                      Decline
                    </Button>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </main>
  );
}

export default PublicSign;
